import { Auth0Client } from '@auth0/auth0-spa-js';

import { registerLogoutHandler } from '@spotify/confidence-core-react';

import { Auth0ProviderConfig } from '../config/types';
import { detachPopupOpener, pollForOAuthCallback } from '../utils/popupOAuth';

export type ConnectedAccount = {
  id: string;
  connection: string;
  access_type?: string;
  scopes?: string[];
  created_at?: string;
  user_info?: {
    email?: string;
    name?: string;
  };
};

export type AccountLinkStep =
  | 'auth0_token'
  | 'fetch_accounts'
  | 'delete_existing_link'
  | 'initiate_link'
  | 'provider_popup'
  | 'complete_link';

export type AuthorizationParams = {
  audience: string;
  scope: string;
};

export type EnsureAuthOptions = {
  authorizationParams?: AuthorizationParams;
  popup?: Window;
  skipCache?: boolean;
};

export type InitiateAccountLinkResult = {
  connectUri: string;
  authSession: string;
  token: string;
  oauthState: string;
};

export type LinkAccountOptions = {
  forceRelink?: boolean;
  popup: Window;
  onStep?: (step: AccountLinkStep) => void;
};

export type UseAuth0AccountLinkResult = {
  linkAccount: (options: LinkAccountOptions) => Promise<{
    linked: boolean;
    failureReason?: string;
    failureDebugMessage?: string;
  }>;
  unlinkAccount: () => Promise<void>;
  isConnected: boolean;
  connectedAccount: ConnectedAccount | undefined;
  checkConnection: () => Promise<boolean>;
  clearAuthCache: () => Promise<void>;
};

class MyAccountRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'MyAccountRequestError';
  }
}

export class OAuthStateMismatchError extends Error {
  constructor() {
    super('OAuth state mismatch');
    this.name = 'OAuthStateMismatchError';
  }
}

export class MissingConnectCodeError extends Error {
  constructor() {
    super('No connect_code received from OAuth callback');
    this.name = 'MissingConnectCodeError';
  }
}

const POPUP_BLOCKED_MESSAGE =
  'Popup blocked. Please allow popups for this site and try again.';
const CONNECTION_CANCELLED_MESSAGE = 'Popup closed.';
const CONNECTION_TIMED_OUT_MESSAGE = 'Connection timed out. Please try again.';
const CONNECTION_VERIFICATION_FAILED_MESSAGE =
  'Connection could not be verified. Please try again.';
const GENERIC_CONNECT_FAILED_MESSAGE =
  'Failed to connect account. Please try again.';

export function isTokenVaultPropagationError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err);
  const normalized = message.toLowerCase();
  return (
    normalized.includes('the user is not found') ||
    normalized.includes('federated connection refresh token not found')
  );
}

export function isPopupBlockedError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err);
  return (
    message.includes('Popup blocked') ||
    message.includes('Unable to open a popup') ||
    message.includes('window.open returned `null`')
  );
}

const CANCELLED_ERROR_CODES = [
  'cancelled',
  'popup_cancelled',
  'popup_closed_by_user',
];

function isCancelledError(err: unknown): boolean {
  if (isRecord(err) && typeof err.error === 'string') {
    return CANCELLED_ERROR_CODES.includes(err.error);
  }
  return err instanceof Error && err.message === 'cancelled';
}

function isTimeoutError(err: unknown): boolean {
  return err instanceof Error && err.message === 'OAuth flow timed out';
}

function getDebugMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function getAuth0AccountLinkErrorMessage(
  err: unknown,
  fallback = GENERIC_CONNECT_FAILED_MESSAGE,
): string {
  if (isCancelledError(err)) {
    return CONNECTION_CANCELLED_MESSAGE;
  }
  if (isTimeoutError(err)) {
    return CONNECTION_TIMED_OUT_MESSAGE;
  }
  if (isPopupBlockedError(err)) {
    return POPUP_BLOCKED_MESSAGE;
  }
  if (err instanceof OAuthStateMismatchError) {
    return CONNECTION_VERIFICATION_FAILED_MESSAGE;
  }
  if (err instanceof MissingConnectCodeError) {
    return CONNECTION_VERIFICATION_FAILED_MESSAGE;
  }
  return fallback;
}

function isMyAccountNotFoundError(err: unknown): boolean {
  return err instanceof MyAccountRequestError && err.status === 404;
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

async function getErrorMessage(
  response: Response,
  fallback: string,
): Promise<string> {
  const errorData: unknown = await response.json().catch(() => undefined);
  if (isRecord(errorData) && typeof errorData.message === 'string') {
    return errorData.message;
  }
  return fallback;
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  delays: readonly number[],
  isRetryable: (err: unknown) => boolean,
  onRetry?: () => Promise<void> | void,
): Promise<T> {
  for (let attempt = 0; attempt <= delays.length; attempt++) {
    try {
      return await fn();
    } catch (err) {
      if (!isRetryable(err) || attempt === delays.length) throw err;
      await onRetry?.();
      await delay(delays[attempt]);
    }
  }
  throw new Error('unreachable');
}

const CONNECT_RETRY_DELAYS_MS = [500, 1_500];
const ACCOUNTS_CACHE_TTL_MS = 30_000;

const authClients = new Map<string, Auth0Client>();
const connectedStorageKeys = new Set<string>();
let unregisterLogoutHandler: (() => void) | undefined;

type AccountsCache = { accounts: ConnectedAccount[]; fetchedAt: number };
const accountsCache = new Map<string, AccountsCache>();

export async function clearAuth0AccountLinkCache(): Promise<void> {
  const clients = Array.from(authClients.values());
  authClients.clear();
  accountsCache.clear();
  connectedStorageKeys.forEach(key => localStorage.removeItem(key));

  await Promise.allSettled(
    clients.map(client => client.logout({ openUrl: false })),
  );
}

function ensureLogoutHandlerRegistered(): void {
  if (unregisterLogoutHandler) return;
  unregisterLogoutHandler = registerLogoutHandler(clearAuth0AccountLinkCache);
}

export function getAuthClient(config: Auth0ProviderConfig): Auth0Client {
  connectedStorageKeys.add(config.connectedStorageKey);
  const cacheKey = `${config.domain}:${config.clientId}:${config.connection}`;
  const existing = authClients.get(cacheKey);
  if (existing) return existing;

  const client = new Auth0Client({
    domain: config.domain,
    clientId: config.clientId,
    authorizationParams: {
      audience: config.myAccountAudience,
      scope: 'openid profile email',
      redirect_uri: window.location.origin,
    },
  });

  authClients.set(cacheKey, client);
  return client;
}

export function myAccountUrl(
  config: Auth0ProviderConfig,
  path: string,
): string {
  const base = config.myAccountAudience.replace(/\/$/, '');
  return `${base}/v1/connected-accounts/${path}`;
}

export function getMyAccountAuthParams(
  config: Auth0ProviderConfig,
): AuthorizationParams {
  return {
    audience: config.myAccountAudience,
    scope:
      'openid profile email read:me:connected_accounts create:me:connected_accounts delete:me:connected_accounts',
  };
}

export async function ensureAuth(
  client: Auth0Client,
  options: EnsureAuthOptions = {},
): Promise<string> {
  const { authorizationParams, popup, skipCache } = options;
  try {
    return await client.getTokenSilently({
      authorizationParams,
      timeoutInSeconds: 10,
      ...(skipCache ? { cacheMode: 'off' as const } : {}),
    });
  } catch {
    await client.loginWithPopup(
      { authorizationParams },
      popup
        ? { popup, closePopup: false, timeoutInSeconds: 60 }
        : { timeoutInSeconds: 60 },
    );
    return client.getTokenSilently({
      authorizationParams,
      timeoutInSeconds: 30,
    });
  }
}

function accountsCacheKey(config: Auth0ProviderConfig): string {
  return `${config.domain}:${config.connection}`;
}

export function invalidateAccountsCache(config: Auth0ProviderConfig): void {
  accountsCache.delete(accountsCacheKey(config));
}

export async function fetchAccountLinks(
  config: Auth0ProviderConfig,
  token: string,
  { skipCache = false }: { skipCache?: boolean } = {},
): Promise<ConnectedAccount[]> {
  const cacheKey = accountsCacheKey(config);
  if (!skipCache) {
    const cached = accountsCache.get(cacheKey);
    if (cached && Date.now() - cached.fetchedAt < ACCOUNTS_CACHE_TTL_MS) {
      return cached.accounts;
    }
  }

  const response = await fetch(myAccountUrl(config, 'accounts'), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch connected accounts: ${response.status}`);
  }
  const data = await response.json();
  const accounts: ConnectedAccount[] = data.accounts ?? [];
  accountsCache.set(cacheKey, { accounts, fetchedAt: Date.now() });
  return accounts;
}

async function initiateAccountLink(
  config: Auth0ProviderConfig,
  token: string,
): Promise<{
  connectUri: string;
  authSession: string;
  oauthState: string;
}> {
  const oauthState = crypto.randomUUID();
  const response = await fetch(myAccountUrl(config, 'connect'), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      connection: config.connection,
      redirect_uri: window.location.origin,
      state: oauthState,
      scopes: config.scopes.split(' ').filter(Boolean),
    }),
  });

  if (!response.ok) {
    throw new MyAccountRequestError(
      await getErrorMessage(
        response,
        `Failed to initiate account link: ${response.status}`,
      ),
      response.status,
    );
  }

  const data = await response.json();
  if (!data.connect_uri || !data.auth_session) {
    throw new Error(`Initiate failed: ${JSON.stringify(data)}`);
  }

  const ticket = data.connect_params?.ticket || data.ticket;
  if (!ticket) {
    throw new Error('No ticket in connect response');
  }

  return {
    connectUri: `${data.connect_uri}?ticket=${encodeURIComponent(ticket)}`,
    authSession: data.auth_session,
    oauthState,
  };
}

export async function initiateAccountLinkWithRetry(
  config: Auth0ProviderConfig,
  client: Auth0Client,
  initialToken: string,
): Promise<InitiateAccountLinkResult> {
  let token = initialToken;
  return retryWithBackoff(
    async () => ({
      ...(await initiateAccountLink(config, token)),
      token,
    }),
    CONNECT_RETRY_DELAYS_MS,
    isMyAccountNotFoundError,
    async () => {
      token = await ensureAuth(client, {
        authorizationParams: getMyAccountAuthParams(config),
        skipCache: true,
      });
    },
  );
}

export async function deleteConnectedAccount(
  config: Auth0ProviderConfig,
  token: string,
  accountId: string,
): Promise<void> {
  const response = await fetch(
    myAccountUrl(config, `accounts/${encodeURIComponent(accountId)}`),
    {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  if (!response.ok) {
    throw new Error(`Failed to delete connected account: ${response.status}`);
  }
  invalidateAccountsCache(config);
}

export function clearStoredConnected(config: Auth0ProviderConfig): void {
  connectedStorageKeys.add(config.connectedStorageKey);
  localStorage.removeItem(config.connectedStorageKey);
}

export function getFailureResult(
  substep: AccountLinkStep,
  err: unknown,
): { linked: false; failureReason: string; failureDebugMessage: string } {
  return {
    linked: false,
    failureReason: getAuth0AccountLinkErrorMessage(err),
    failureDebugMessage: `${substep}:${getDebugMessage(err)}`,
  };
}

export {
  ensureLogoutHandlerRegistered,
  detachPopupOpener,
  pollForOAuthCallback,
};
